import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { usePersistentSet, usePersistentValue } from '../../hooks/usePersistentState';
import { useTableSort } from '../../hooks/useTableSort';
import { normalizeName, buildSearchKey } from '../../utils/personTable';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import { EditableCell } from '../../components/EditableCell';
import {
  listPeople,
  createPerson,
  patchPerson,
  listCostPools,
} from '../../api/tdabc/people';
import PageHeader from '../../components/PageHeader';
import './PeoplePage.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
function errorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}

// ── Cost pool dropdown ────────────────────────────────────────────────────────
function CostPoolCell({ value, pools, onSave }) {
  const p = useP();
  function handleChange(e) {
    const v = e.target.value || null;
    onSave({ cost_pool_id: v });
  }
  const isUnassigned = !value;
  return (
    <select
      className={`pp-pool-select${isUnassigned ? ' pp-pool-select--empty' : ''}`}
      value={value || ''}
      onChange={handleChange}
    >
      <option value="">{p('— Non affecté —')}</option>
      {pools.map(pool => (
        <option key={pool.id} value={pool.id}>
          {pool.is_overhead_pool ? '⊙ ' : ''}{pool.name}
        </option>
      ))}
    </select>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PeoplePage({ activeTenant, addToast, year, embedded, poolsVersion = 0 }) {
  const { t } = useTranslation();
  const p = useP();
  const [pools, setPools] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const tid = activeTenant?.id;
  // Filter by cost pool — Set of active tags; empty Set = show all
  const [poolFilter, setPoolFilter] = usePersistentSet(`tdabc_people_pool_${tid}`);

  // Bar mode : 'filter' (default, click on pill = filter rows) | 'brush' (click
  // on pill = arme un pinceau ; click sur une rangée = assigne ce pool à la
  // personne, en un clic). Permet l'assignation multi-personnes super rapide.
  const [mode, setMode] = usePersistentValue(`tdabc_people_mode_${tid}`, 'filter');
  const [brushPoolId, setBrushPoolId] = useState(null);  // string|null|'__unassigned__'

  // Reset brush quand tenant change (poolFilter et mode sont persistés par tenant)
  useEffect(() => {
    setBrushPoolId(null);
  }, [activeTenant]);

  function togglePoolTag(key) {
    setPoolFilter(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function handlePillClick(key) {
    if (mode === 'filter') {
      togglePoolTag(key);
    } else {
      // mode brush : re-clic sur le pool armé = désarme.
      setBrushPoolId(prev => prev === key ? null : key);
    }
  }

  function handleRowClick(person) {
    if (mode !== 'brush' || !brushPoolId) return;
    const nextPoolId = brushPoolId === '__unassigned__' ? null : brushPoolId;
    if (person.cost_pool_id === nextPoolId) return;  // déjà assigné
    handlePatch(person.id, { cost_pool_id: nextPoolId });
  }

  // Tri partagé (colonne + direction persistées). La colonne « cost_pool »
  // se trie sur le nom du groupe ; les textes sont normalisés (accents).
  const { sortCol: sortBy, sortDir, handleSort, sortRows } = useTableSort(`tdabc_people_sort_${tid}`, 'person_name');
  const [search, setSearch] = useState('');

  function getSortValue(person, col) {
    if (col === 'cost_pool') {
      const pool = pools.find(x => x.id === person.cost_pool_id);
      return normalizeName(pool?.name) || null;
    }
    const v = person[col];
    if (v === '' || v == null) return null;
    return typeof v === 'string' ? normalizeName(v) : v;
  }

  // Groupes ressources — rechargés quand ils changent ailleurs (ajout/suppression
  // dans « Groupes ressources » → poolsVersion bumpé par le hub).
  useEffect(() => {
    if (!activeTenant) { setPools([]); return; }
    listCostPools(activeTenant.id)
      .then(res => setPools(res.data || []))
      .catch(err => addToast(errorMessage(err, p('Échec du chargement des groupes')), 'error'));
  }, [activeTenant, poolsVersion]); // eslint-disable-line

  // Personnes — sur tenant / année.
  useEffect(() => {
    if (!activeTenant) { setPeople([]); return; }
    setLoading(true);
    listPeople(activeTenant.id, year)
      .then(res => setPeople(res.data || []))
      .catch(err => addToast(errorMessage(err, p('Échec du chargement des données')), 'error'))
      .finally(() => setLoading(false));
  }, [activeTenant, year]); // eslint-disable-line

  async function handleAdd(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const res = await createPerson(activeTenant.id, { person_name: name });
      if (res.data) {
        setPeople(prev => [...prev, res.data]);
        setNewName('');
        setShowAdd(false);
      }
    } catch (err) {
      addToast(errorMessage(err, p('Échec de la création de la personne')), 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handlePatch(id, patch) {
    try {
      const res = await patchPerson(activeTenant.id, id, patch, year);
      if (res.data) {
        setPeople(prev => prev.map(person => person.id === id ? res.data : person));
      }
    } catch (err) {
      addToast(errorMessage(err, p('Échec de l’enregistrement')), 'error');
    }
  }

  // Sorted + filtered view of the people list
  const q = normalizeName(search.trim());
  const sortedPeople = sortRows(people, getSortValue)
    .filter(person => {
      if (poolFilter.size > 0) {
        if (!person.cost_pool_id) { if (!poolFilter.has('__unassigned__')) return false; }
        else if (!poolFilter.has(person.cost_pool_id)) return false;
      }
      if (q) {
        const poolName = pools.find(x => x.id === person.cost_pool_id)?.name;
        const key = buildSearchKey(person.person_name, person.dept_or_cc, person.function, poolName);
        if (!key.includes(q)) return false;
      }
      return true;
    });

  function SortableTh({ col, children, align = 'left', className = '' }) {
    const active = sortBy === col;
    const arrow = active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
    return (
      <th
        className={`pp-th-sortable${active ? ' pp-th-sortable--active' : ''} ${className}`}
        style={{ textAlign: align }}
        onClick={() => handleSort(col)}
        title={p('Cliquer pour trier')}
      >
        {children}{arrow}
      </th>
    );
  }

  if (!activeTenant) {
    return (
      <main className="page-table-layout is-fluid is-compact">
        <div className="page-table-empty">{p('Sélectionner une entreprise.')}</div>
      </main>
    );
  }

  // Vue Cost Pool assignment : table à 4 colonnes (Personnel + Dept + Function
  // + Cost pool). Dept et Function sont en lecture seule, ils servent à
  // identifier rapidement la personne lors de l'assignation. Toutes les autres
  // colonnes de valeurs (salary, bonus, costs, dates…) sont gérées ailleurs.
  const TOTAL_COLS = 4;

  return (
    <main className={`page-table-layout pp-layout is-compact${embedded ? ' pp-layout--embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={p('Affectation des personnes')}
        />
      )}

      <CardShell
        flush
        className="pp-card"
        primaryAction={(
          <>
            <button
              className={`pp-mode-toggle${mode === 'brush' ? ' is-on' : ''}`}
              onClick={() => {
                setMode(m => m === 'brush' ? 'filter' : 'brush');
                setBrushPoolId(null);
              }}
              title={mode === 'brush'
                ? p('Désactiver le mode affectation (retour au filtre)')
                : p('Activer le mode affectation : cliquer un groupe ci-dessus pour armer, puis cliquer les lignes pour affecter en un clic')}
            >
              {mode === 'brush' ? p('🖌 Mode affectation · ON') : p('🖌 Mode affectation')}
            </button>
            <button
              className="p-btn p-btn--primary"
              onClick={() => setShowAdd(v => !v)}
            >
              {showAdd ? t('common:actions.cancel') : p('+ Ajouter une personne')}
            </button>
          </>
        )}
        filters={(
          <>
          <FilterIcon />
          <span className="pp-filter-label">{p('Groupe ressources')}</span>
          <div className="pp-filter-tags">
            {(() => {
              const count = people.filter(person => !person.cost_pool_id).length;
              if (count === 0) return null;
              const isFilterActive = mode === 'filter' && poolFilter.has('__unassigned__');
              const isBrushActive  = mode === 'brush'  && brushPoolId === '__unassigned__';
              return (
                <button
                  key="__unassigned__"
                  className={`pp-tag${isFilterActive ? ' pp-tag--active' : ''}${isBrushActive ? ' pp-tag--brush' : ''}`}
                  onClick={() => handlePillClick('__unassigned__')}
                  title={mode === 'brush' ? p('Cliquer pour armer — puis cliquer les lignes à désaffecter') : p('Filtrer les non affectés')}
                >
                  {p('Non affecté')}
                  <span className="pp-tag-count">{count}</span>
                </button>
              );
            })()}
            {pools.map(pool => {
              const count = people.filter(person => person.cost_pool_id === pool.id).length;
              const isFilterActive = mode === 'filter' && poolFilter.has(pool.id);
              const isBrushActive  = mode === 'brush'  && brushPoolId === pool.id;
              return (
                <button
                  key={pool.id}
                  className={
                    `pp-tag` +
                    (isFilterActive ? ' pp-tag--active' : '') +
                    (isBrushActive  ? ' pp-tag--brush'  : '') +
                    (pool.is_overhead_pool ? ' pp-tag--overhead' : '')
                  }
                  onClick={() => handlePillClick(pool.id)}
                  title={mode === 'brush' ? `${p('Armer ')}${pool.name}${p(' — puis cliquer les lignes à affecter')}` : `${p('Filtrer ')}${pool.name}`}
                >
                  {pool.is_overhead_pool ? '⊙ ' : ''}{pool.name}
                  <span className="pp-tag-count">{count}</span>
                </button>
              );
            })}
          </div>
          <FilterCount count={(mode === 'filter' ? poolFilter.size : 0) + (search.trim() ? 1 : 0)}
                       onClear={() => { setPoolFilter(new Set()); setSearch(''); }} />
          </>
        )}
        search={<SearchField value={search} onChange={setSearch} placeholder={p('Rechercher…')} />}
        filterCount={(mode === 'filter' ? poolFilter.size : 0) + (search.trim() ? 1 : 0)}
        collapsible storageKey="tdabc-people-assign"
      >

      {mode === 'brush' && (
        <div className="pp-brush-hint">
          {brushPoolId
            ? <>{p('Pinceau armé : ')}<strong>
                {brushPoolId === '__unassigned__'
                  ? p('Désaffecter')
                  : (pools.find(pool => pool.id === brushPoolId)?.name || '')}
              </strong>{p(' — cliquer une ligne pour affecter en un clic. Recliquer le tag pour désarmer.')}</>
            : <>{p('Cliquer un groupe ressources ci-dessus pour armer le pinceau, puis cliquer les lignes pour affecter en un clic.')}</>
          }
        </div>
      )}

      {showAdd && (
        <form className="pp-add-form" onSubmit={handleAdd}>
          <input
            placeholder={p('Nom de la personne (ex. Alice Dupont)')}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            disabled={creating}
            autoFocus
          />
          <button className="p-btn p-btn--primary" type="submit" disabled={!newName.trim() || creating}>
            {creating ? '…' : p('Créer')}
          </button>
          <span className="pp-add-hint">
            {p('Astuce : affecter un groupe ressources, le salaire brut et l’EPT dans le tableau après création.')}
          </span>
        </form>
      )}

      {loading && <p className="page-table-loading">{t('common:status.loading')}</p>}

      {!loading && (
        <div className="pp-table-scroll">
          <table className={`page-table pp-table${mode === 'brush' && brushPoolId ? ' pp-table--brush-armed' : ''}`}>
            <thead>
              <tr>
                <SortableTh col="person_name" className="pp-th-sticky pp-th-sticky-1">{p('Personne')}</SortableTh>
                <SortableTh col="dept_or_cc">{t('phrases:p_3d7c87c2af')}</SortableTh>
                <SortableTh col="function">{p('Fonction')}</SortableTh>
                <SortableTh col="cost_pool">{p('Groupe ressources')}</SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedPeople.length === 0 && (
                <tr>
                  <td colSpan={TOTAL_COLS} className="page-table-empty">{p('Aucune personne. Cliquez « + Ajouter une personne » pour commencer.')}</td>
                </tr>
              )}
              {sortedPeople.map(person => (
                <tr
                  key={person.id}
                  className={
                    (!person.cost_pool_assigned ? 'pp-row--unassigned' : '') +
                    (mode === 'brush' && brushPoolId ? ' pp-row--brushable' : '')
                  }
                  onClick={() => handleRowClick(person)}
                >
                  <td className="pp-td-sticky pp-td-sticky-1">
                    {mode === 'brush' ? (
                      <span className="p-cell-text">{person.person_name}</span>
                    ) : (
                      <EditableCell value={person.person_name} field="person_name"
                        onSave={patch => handlePatch(person.id, patch)} />
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    {/* Département dérivé du contrat actif (source unique) — lecture seule. */}
                    <span className="p-cell-text" style={person.dept_or_cc ? undefined : { color: 'var(--color-text-muted)' }}>{person.dept_or_cc || '—'}</span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <EditableCell value={person.function || ''} field="function"
                      onSave={patch => handlePatch(person.id, patch)} />
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <CostPoolCell value={person.cost_pool_id} pools={pools}
                      onSave={patch => handlePatch(person.id, patch)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </CardShell>

      <p className="pp-disclaimer">
        {p('Cette vue affecte chaque personne à son groupe ressources. Active le ')}
        <em>{p('mode affectation')}</em>
        {p(' puis clique un groupe ci-dessus pour armer un pinceau — chaque clic sur une ligne affecte')}
        {p(' ce groupe en un coup. Les colonnes de coût (salaire, charges sociales, indemnités…) sont')}
        {p(' gérées dans le module People.')}
      </p>
    </main>
  );
}
